const MAX_GROUPED_AXIS_TICKS = 8

export type AxisGroupingInterval =
  | 'weekly'
  | 'monthly'
  | 'bimonthly'
  | 'quarterly'
  | 'semiannual'

export type ChartAxisGroupingSelection = AxisGroupingInterval

export const DEFAULT_CHART_AXIS_GROUPING: AxisGroupingInterval = 'weekly'

export const CHART_AXIS_GROUPING_OPTIONS: Array<{
  value: AxisGroupingInterval
  label: string
}> = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'bimonthly', label: 'Bi-monthly' },
  { value: 'quarterly', label: 'Quarterly' },
]

type ChartSeries = {
  data: Array<{ x: string | Date; [key: string]: unknown }>
}

export type AxisGroupingConfig = {
  isGrouped: boolean
  interval?: AxisGroupingInterval
  tickDomainKeys?: string[]
  tickLabelsByDomainKey?: Map<string, Date>
}

export type BottomAxisLayout = {
  tickIndices: number[]
  tickDomainKeys: string[]
  xScaleMin: number
  xScaleMax: number
  displayIndexByRawIndex: number[]
  grouping: AxisGroupingConfig
}

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000
const MS_PER_MONTH = 24 * 60 * 60 * 1000 * 30.4375

const GROUPING_INTERVALS: AxisGroupingInterval[] = [
  'weekly',
  'monthly',
  'bimonthly',
  'quarterly',
  'semiannual',
]

export const toChartDate = (value: string | Date): Date =>
  value instanceof Date ? value : new Date(value)

export const getSortedUniqueXDomain = (data: ChartSeries[]): string[] => {
  const uniqueDates = [
    ...new Set(
      data.flatMap(series =>
        series.data.map(point =>
          point.x instanceof Date ? point.x.toISOString() : String(point.x)
        )
      )
    ),
  ]

  return uniqueDates.sort(
    (a, b) => toChartDate(a).getTime() - toChartDate(b).getTime()
  )
}

const getDateRangeSpanMs = (sortedXDomain: string[]): number => {
  if (sortedXDomain.length < 2) {
    return 0
  }

  return (
    toChartDate(sortedXDomain[sortedXDomain.length - 1]).getTime() -
    toChartDate(sortedXDomain[0]).getTime()
  )
}

const getGroupingIntervalForSpan = (spanMs: number): AxisGroupingInterval => {
  /* istanbul ignore start -- callers always pass a forced interval */
  const weeks = spanMs / MS_PER_WEEK
  const months = spanMs / MS_PER_MONTH

  if (weeks <= 8) {
    return 'weekly'
  }

  if (months <= 6) {
    return 'monthly'
  }

  if (months <= 18) {
    return 'bimonthly'
  }

  if (months <= 36) {
    return 'quarterly'
  }

  return 'semiannual'
  /* istanbul ignore end */
}

const startOfUtcMonth = (date: Date) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1))

const startOfUtcWeek = (date: Date) => {
  const day = date.getUTCDay()
  const diffToMonday = day === 0 ? -6 : 1 - day

  return new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate() + diffToMonday
    )
  )
}

const addIntervalDays = (date: Date, dayStep: number) =>
  new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate() + dayStep
    )
  )

const addIntervalMonths = (date: Date, monthStep: number) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + monthStep, 1))

const getMonthStep = (interval: Exclude<AxisGroupingInterval, 'weekly'>) => {
  switch (interval) {
    case 'monthly':
      return 1
    case 'bimonthly':
      return 2
    case 'quarterly':
      return 3
    case 'semiannual':
      return 6
  }
}

const alignToIntervalStart = (
  date: Date,
  interval: AxisGroupingInterval
): Date => {
  const year = date.getUTCFullYear()
  const month = date.getUTCMonth()

  switch (interval) {
    case 'weekly':
      return startOfUtcWeek(date)
    case 'semiannual':
      return new Date(Date.UTC(year, month < 6 ? 0 : 6, 1))
    case 'quarterly':
      return new Date(Date.UTC(year, Math.floor(month / 3) * 3, 1))
    case 'bimonthly':
      return new Date(Date.UTC(year, Math.floor(month / 2) * 2, 1))
    case 'monthly':
    default:
      return startOfUtcMonth(date)
  }
}

const generateTicksForInterval = (
  minDate: Date,
  maxDate: Date,
  interval: AxisGroupingInterval
): Date[] => {
  const ticks: Date[] = []
  let current = alignToIntervalStart(minDate, interval)

  while (current.getTime() <= maxDate.getTime()) {
    ticks.push(new Date(current))
    current =
      interval === 'weekly'
        ? addIntervalDays(current, 7)
        : addIntervalMonths(current, getMonthStep(interval))
  }

  if (!ticks.length) {
    ticks.push(alignToIntervalStart(minDate, interval))
  }

  return ticks
}

const isDateInCalendarPeriod = (
  date: Date,
  calendarTick: Date,
  interval: AxisGroupingInterval
): boolean => {
  const tickYear = calendarTick.getUTCFullYear()
  const tickMonth = calendarTick.getUTCMonth()
  const dateYear = date.getUTCFullYear()
  const dateMonth = date.getUTCMonth()

  switch (interval) {
    case 'weekly':
      return (
        startOfUtcWeek(date).getTime() ===
        startOfUtcWeek(calendarTick).getTime()
      )
    case 'semiannual':
      return (
        dateYear === tickYear &&
        Math.floor(dateMonth / 6) === Math.floor(tickMonth / 6)
      )
    case 'quarterly':
      return (
        dateYear === tickYear &&
        Math.floor(dateMonth / 3) === Math.floor(tickMonth / 3)
      )
    case 'bimonthly':
      return (
        dateYear === tickYear &&
        Math.floor(dateMonth / 2) === Math.floor(tickMonth / 2)
      )
    case 'monthly':
    default:
      return dateYear === tickYear && dateMonth === tickMonth
  }
}

const findDomainKeyForCalendarTick = (
  calendarTick: Date,
  sortedXDomain: string[],
  interval: AxisGroupingInterval
): string | undefined => {
  if (!sortedXDomain.length) {
    return undefined
  }

  const tickTime = calendarTick.getTime()
  const inPeriod = sortedXDomain.filter(domainKey =>
    isDateInCalendarPeriod(toChartDate(domainKey), calendarTick, interval)
  )
  const candidates = inPeriod.length ? inPeriod : sortedXDomain

  return candidates.reduce((best, domainKey) => {
    const keyDistance = Math.abs(toChartDate(domainKey).getTime() - tickTime)
    const bestDistance = Math.abs(toChartDate(best).getTime() - tickTime)

    return keyDistance < bestDistance ? domainKey : best
  })
}

const buildGroupedAxisTicks = (
  sortedXDomain: string[],
  options?: {
    forcedInterval?: AxisGroupingInterval
    allowCoarsening?: boolean
  }
) => {
  const minDate = toChartDate(sortedXDomain[0])
  const maxDate = toChartDate(sortedXDomain[sortedXDomain.length - 1])
  const spanMs = getDateRangeSpanMs(sortedXDomain)
  const allowCoarsening = options?.allowCoarsening ?? !options?.forcedInterval

  let interval = options?.forcedInterval ?? getGroupingIntervalForSpan(spanMs)
  let calendarTicks = generateTicksForInterval(minDate, maxDate, interval)

  /* istanbul ignore start -- forced interval skips automatic coarsening */
  while (
    allowCoarsening &&
    calendarTicks.length > MAX_GROUPED_AXIS_TICKS &&
    interval !== 'semiannual'
  ) {
    interval =
      GROUPING_INTERVALS[GROUPING_INTERVALS.indexOf(interval) + 1] ??
      'semiannual'
    calendarTicks = generateTicksForInterval(minDate, maxDate, interval)
  }
  /* istanbul ignore end */

  const tickLabelsByDomainKey = new Map<string, Date>()
  const tickDomainKeys: string[] = []

  calendarTicks.forEach(calendarTick => {
    const domainKey = findDomainKeyForCalendarTick(
      calendarTick,
      sortedXDomain,
      interval
    )

    if (!domainKey || tickLabelsByDomainKey.has(domainKey)) {
      return
    }

    tickLabelsByDomainKey.set(domainKey, calendarTick)
    tickDomainKeys.push(domainKey)
  })

  return { interval, tickDomainKeys, tickLabelsByDomainKey }
}

const getAxisGroupingConfig = (
  sortedXDomain: string[],
  groupingMode: ChartAxisGroupingSelection
): AxisGroupingConfig => {
  const groupedTicks = buildGroupedAxisTicks(sortedXDomain, {
    forcedInterval: groupingMode,
  })

  return {
    isGrouped: true,
    interval: groupedTicks.interval,
    tickDomainKeys: groupedTicks.tickDomainKeys,
    tickLabelsByDomainKey: groupedTicks.tickLabelsByDomainKey,
  }
}

const buildXDomainIndexMap = (xDomain: string[]) =>
  new Map(xDomain.map((domainKey, index) => [domainKey, index]))

const computeMaxLabelCellGap = (snappedIndices: number[]): number => {
  if (snappedIndices.length < 2) {
    return 1
  }

  return snappedIndices.slice(1).reduce((maxGap, index, previousIndex) => {
    const gap = index - snappedIndices[previousIndex]
    return Math.max(maxGap, gap)
  }, 1)
}

const buildDisplayIndexByRawIndex = (
  xDomainLength: number,
  snappedIndices: number[],
  maxCellGap: number
): number[] => {
  if (!xDomainLength) {
    return []
  }

  if (!snappedIndices.length) {
    return Array.from({ length: xDomainLength }, (_, index) => index)
  }

  if (snappedIndices.length === 1) {
    const anchor = snappedIndices[0]
    return Array.from(
      { length: xDomainLength },
      (_, rawIndex) => rawIndex - anchor
    )
  }

  const rawToDisplay = (rawIndex: number): number => {
    const firstRaw = snappedIndices[0]
    const lastRaw = snappedIndices[snappedIndices.length - 1]

    if (rawIndex <= firstRaw) {
      const slope = maxCellGap / (snappedIndices[1] - firstRaw)
      return (rawIndex - firstRaw) * slope
    }

    if (rawIndex >= lastRaw) {
      const prevRaw = snappedIndices[snappedIndices.length - 2]
      const lastLabelIndex = snappedIndices.length - 1
      const slope = maxCellGap / (lastRaw - prevRaw)
      return lastLabelIndex * maxCellGap + (rawIndex - lastRaw) * slope
    }

    for (let labelIdx = 0; labelIdx < snappedIndices.length - 1; labelIdx++) {
      const rawStart = snappedIndices[labelIdx]
      const rawEnd = snappedIndices[labelIdx + 1]

      if (rawIndex >= rawStart && rawIndex <= rawEnd) {
        const fraction = (rawIndex - rawStart) / (rawEnd - rawStart)
        return labelIdx * maxCellGap + fraction * maxCellGap
      }
    }

    return rawIndex
  }

  return Array.from({ length: xDomainLength }, (_, rawIndex) =>
    rawToDisplay(rawIndex)
  )
}

export const resolveBottomAxisLayout = (
  xDomain: string[],
  bottomTickValues?: boolean,
  groupingSelection: ChartAxisGroupingSelection = DEFAULT_CHART_AXIS_GROUPING
): BottomAxisLayout | undefined => {
  if (bottomTickValues !== true || !xDomain.length) {
    return undefined
  }

  const grouping = getAxisGroupingConfig(xDomain, groupingSelection)
  const tickDomainKeys = grouping.tickDomainKeys ?? []

  if (!tickDomainKeys.length) {
    return undefined
  }

  const indexByDomain = buildXDomainIndexMap(xDomain)
  const snappedIndices = tickDomainKeys.map(
    domainKey => indexByDomain.get(domainKey) ?? 0
  )
  const maxCellGap = computeMaxLabelCellGap(snappedIndices)
  const tickIndices = tickDomainKeys.map((_, index) => index * maxCellGap)
  const displayIndexByRawIndex = buildDisplayIndexByRawIndex(
    xDomain.length,
    snappedIndices,
    maxCellGap
  )
  const xScaleMin = Math.min(0, ...displayIndexByRawIndex)
  const xScaleMax = Math.max(
    tickIndices[tickIndices.length - 1] ?? 0,
    ...displayIndexByRawIndex
  )

  return {
    tickIndices,
    tickDomainKeys,
    xScaleMin,
    xScaleMax,
    displayIndexByRawIndex,
    grouping,
  }
}

export const resolveChartAxis = (
  data: ChartSeries[],
  bottomTickValues?: boolean,
  groupingSelection: ChartAxisGroupingSelection = DEFAULT_CHART_AXIS_GROUPING
) => {
  const xDomain = getSortedUniqueXDomain(data)
  const bottomAxisLayout = resolveBottomAxisLayout(
    xDomain,
    bottomTickValues,
    groupingSelection
  )

  return { xDomain, bottomAxisLayout }
}

export const getBottomAxisTickLabelDate = (
  layout: BottomAxisLayout,
  tickValue: number
): Date | undefined => {
  const labelIndex = layout.tickIndices.indexOf(tickValue)

  if (labelIndex < 0) {
    return undefined
  }

  const domainKey = layout.tickDomainKeys[labelIndex]

  return (
    layout.grouping.tickLabelsByDomainKey?.get(domainKey) ??
    toChartDate(domainKey)
  )
}

const resolveXToDomainKey = (xDomain: string[]) => {
  const byTime = new Map(
    xDomain.map(domainKey => [toChartDate(domainKey).getTime(), domainKey])
  )

  return (value: string | Date) =>
    byTime.get(toChartDate(value).getTime()) ?? toChartDate(value).toISOString()
}

export const mapSeriesToIndexedX = (
  data: ChartSeries[],
  xDomain: string[],
  displayIndexByRawIndex?: number[]
) => {
  const toDomainKey = resolveXToDomainKey(xDomain)
  const indexByDomain = buildXDomainIndexMap(xDomain)

  return data.map(series => ({
    ...series,
    data: series.data.map(point => {
      const rawIndex = indexByDomain.get(toDomainKey(point.x)) ?? 0

      return {
        ...point,
        x: displayIndexByRawIndex?.[rawIndex] ?? rawIndex,
      }
    }),
  }))
}

const spansMultipleYears = (sortedXDomain: string[]): boolean => {
  const years = new Set(
    sortedXDomain.map(date => toChartDate(date).getUTCFullYear())
  )

  return years.size > 1
}

const formatGroupedAxisDate = (
  date: Date,
  interval: AxisGroupingInterval,
  timeZone?: string
): string => {
  const options: Intl.DateTimeFormatOptions = { timeZone }

  if (interval === 'weekly') {
    return date.toLocaleDateString('en-US', {
      ...options,
      month: 'short',
      day: 'numeric',
    })
  }

  return date.toLocaleDateString('en-US', {
    ...options,
    month: 'short',
    year: 'numeric',
  })
}

export const formatAxisTickLabel = ({
  date,
  sortedXDomain,
  grouping,
  timeZone,
  shortDateFormatter,
}: {
  date: Date
  sortedXDomain: string[]
  grouping: AxisGroupingConfig
  timeZone?: string
  shortDateFormatter: (value: Date, timeZone?: string) => string
}): string => {
  if (grouping.isGrouped && grouping.interval) {
    return formatGroupedAxisDate(date, grouping.interval, timeZone)
  }

  if (spansMultipleYears(sortedXDomain)) {
    return date.toLocaleDateString('en-US', {
      timeZone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return shortDateFormatter(date, timeZone)
}

const CHART_HORIZONTAL_MARGINS = 110
const AXIS_LABEL_MIN_GAP = 12
const AXIS_LABEL_FONT_SIZE = 12

const estimateAxisLabelWidth = (
  label: string,
  fontSize = AXIS_LABEL_FONT_SIZE
): number => {
  const charWidth = fontSize * 0.54

  return Math.ceil(label.length * charWidth) + 8
}

export const getBottomAxisTickLabels = ({
  bottomAxisLayout,
  xDomain,
  grouping,
  timeZone,
  shortDateFormatter,
}: {
  bottomAxisLayout?: BottomAxisLayout
  xDomain: string[]
  grouping: AxisGroupingConfig
  timeZone?: string
  shortDateFormatter: (value: Date, timeZone?: string) => string
}): string[] => {
  if (!bottomAxisLayout?.tickIndices.length) {
    return []
  }

  return bottomAxisLayout.tickIndices
    .map(tickValue => {
      const date = getBottomAxisTickLabelDate(bottomAxisLayout, tickValue)

      if (!date) {
        return ''
      }

      return formatAxisTickLabel({
        date,
        sortedXDomain: xDomain,
        grouping,
        timeZone,
        shortDateFormatter,
      })
    })
    .filter(label => label.length > 0)
}

export const computeHorizontalScrollLayout = ({
  containerWidth,
  tickLabels,
  horizontalMargins = CHART_HORIZONTAL_MARGINS,
}: {
  containerWidth: number
  tickLabels: string[]
  horizontalMargins?: number
}): { needsHorizontalScroll: boolean; minChartWidth: number } => {
  if (!tickLabels.length || containerWidth <= 0) {
    return { needsHorizontalScroll: false, minChartWidth: 0 }
  }

  const maxLabelWidth = Math.max(
    ...tickLabels.map(label => estimateAxisLabelWidth(label))
  )
  const minTickSpacing = maxLabelWidth + AXIS_LABEL_MIN_GAP
  const minPlotWidth = tickLabels.length * minTickSpacing
  const minChartWidth = Math.ceil(minPlotWidth + horizontalMargins)

  return {
    needsHorizontalScroll: minChartWidth > containerWidth,
    minChartWidth,
  }
}

type RankChartSeries = {
  data: Array<{ y?: number | null }>
}

export const getMaxRankFromChartSeries = (
  series: RankChartSeries[]
): number => {
  let maxRank = 1

  series.forEach(chartSeries => {
    chartSeries.data.forEach(point => {
      if (typeof point.y === 'number' && Number.isFinite(point.y)) {
        maxRank = Math.max(maxRank, point.y)
      }
    })
  })

  return Math.ceil(maxRank)
}

export const resolveRankYAxisLayoutFromMax = (
  maxRank: number,
  tickCount = 10
): { max: number; tickValues: number[] } => {
  const dataMax = Math.max(1, Math.ceil(maxRank))

  if (dataMax === 1) {
    return { max: 1, tickValues: [1] }
  }

  const slots = Math.max(2, tickCount)
  const step = Math.max(1, Math.ceil((dataMax - 1) / (slots - 1)))
  const scaleMax = 1 + step * Math.ceil((dataMax - 1) / step)
  const tickValues: number[] = []

  for (let value = 1; value <= scaleMax; value += step) {
    tickValues.push(value)
  }

  return { max: scaleMax, tickValues }
}

export const resolveRankYAxisTicks = (
  maxRank: number,
  tickCount = 10
): number[] => resolveRankYAxisLayoutFromMax(maxRank, tickCount).tickValues

export const resolveRankYAxisLayout = (
  series: RankChartSeries[],
  tickCount = 10
): { max: number; tickValues: number[] } =>
  resolveRankYAxisLayoutFromMax(getMaxRankFromChartSeries(series), tickCount)
